import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, PropTable, Steps, Step, Code, Screenshot } from '../../components/DocComponents'

const toc = [
  { id: 'overview', title: 'Overview', level: 2 as const },
  { id: 'create', title: 'Creating users', level: 2 as const },
  { id: 'roles', title: 'Assigning roles', level: 2 as const },
  { id: 'fields', title: 'User fields', level: 2 as const },
]

export function Users() {
  return (
    <Article toc={toc}>
      <H1>Users</H1>
      <Lead>Users are the people who access Rabbit. Each user belongs to exactly one Business and holds one or more Roles that control what they can do.</Lead>

      <H2 id="overview">Overview</H2>
      <P>A user account is created either through the self-registration flow (which simultaneously creates a new Business) or by an existing admin inside the Console under <strong>Identity &amp; Access › Users</strong>. Every user must verify their email address before they can log in.</P>
      <Callout type="info">The first user of a business is automatically granted the <Code>business-leader</Code> role, which has full permissions across all services.</Callout>

      <H2 id="create">Creating users</H2>
      <Steps>
        <Step n={1} title="Open Users">Go to <strong>Identity &amp; Access › Users</strong> in the sidebar.</Step>
        <Step n={2} title="Click New User">Fill in the full name, email address, and a temporary password.</Step>
        <Step n={3} title="Assign roles">Select one or more roles from the list. At least one role is required.</Step>
        <Step n={4} title="Save">The user receives a verification email and can log in once verified.</Step>
      </Steps>
      <Screenshot alt="The Users page showing a table of users with name, email, roles, and status columns" caption="The Users list shows each user's verification status and assigned roles." />

      <H2 id="roles">Assigning roles</H2>
      <P>Roles can be assigned or removed at any time from the user's detail panel. Changes take effect immediately — the next API call the user makes will use the updated permissions.</P>
      <Callout type="warning">Removing all roles from a user effectively locks them out. Keep at least one role assigned.</Callout>

      <H2 id="fields">User fields</H2>
      <PropTable props={[
        { name: 'fullName', type: 'string', required: true, description: 'Display name shown in the Console and audit logs.' },
        { name: 'email', type: 'string', required: true, description: 'Login email. Must be unique per business.' },
        { name: 'password', type: 'string', required: true, description: 'Minimum 8 characters. Stored as a bcrypt hash.' },
        { name: 'roles', type: 'string[]', required: true, description: 'List of role names to assign. Must exist in this business.' },
        { name: 'emailVerified', type: 'boolean', default: 'false', description: 'Set to true after the user clicks the verification link.' },
      ]} />
    </Article>
  )
}
